import React from 'react';

import { Typography } from 'antd';
import styled from 'styled-components';

import Select from 'components/lib/Select';

import {
  EMAIL_MATCHING_COMMUNICATION_TYPE_OPTIONS,
  EMAIL_MATCHING_DELIVERY_METHOD_LABELS,
  EMAIL_MATCHING_DELIVERY_METHOD_OPTIONS,
  EMAIL_MATCHING_SENDER_TYPE_LABELS,
  EMAIL_MATCHING_SENDER_TYPE_OPTIONS,
} from './constants';

type OptionType = {
  value: number;
  label: string;
};

type Props = {
  selectedCommunityId?: number;
  communityOptions: OptionType[];
  isLoadingCommunities: boolean;
  onCommunityChange: (value: number | string | null | undefined) => void;
  shouldShowCommunityWarning: boolean;
  aiSuggestedCommunityLabel: string | null;
  selectedSenderType?: number;
  onSenderTypeChange: (value: number | string) => void;
  shouldShowSenderTypeWarning: boolean;
  aiSenderType?: number;
  selectedDeliveryMethod?: number;
  onDeliveryMethodChange: (value: number | string) => void;
  shouldShowDeliveryMethodWarning: boolean;
  aiDeliveryMethod?: number;
  selectedEmailTypeValues: number[];
  onEmailTypesSelect: (values: Array<number | string>) => void;
  shouldShowCommunicationTypesWarning: boolean;
  aiSuggestedCommunicationTypesText: string;
  disabled?: boolean;
};

const MatchingFields = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px 16px;
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const FieldLabel = styled(Typography.Text)`
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 4px;
  font-family: var(--font-bold);
`;

const FieldWarning = styled(Typography.Text)`
  display: block;
  margin-top: 4px;
  font-size: 10px;
  color: var(--inactive-orange);
`;

const FullWidthSelect = styled(Select)`
  width: 100%;

  &&&.ant-select-disabled {
    cursor: default;

    .ant-select-selector {
      background: var(--background-gray);
      cursor: default;
    }

    .ant-select-selection-item {
      color: var(--text-primary);
      -webkit-text-fill-color: var(--text-primary);
      opacity: 1;
      cursor: default;
    }

    .ant-select-arrow,
    .ant-select-clear {
      display: none;
    }
  }
`;

const EmailTypesMultiSelect = styled(Select)`
  width: 100%;

  &&&.ant-select {
    .ant-select-selector {
      min-height: 44px;
      align-items: flex-start;
      padding: 5px 8px;
      border-radius: 6px;
      border: 1px solid var(--line-gray);
      box-shadow: none;
    }

    .ant-select-selection-overflow {
      gap: 6px 4px;
    }

    .ant-select-selection-item {
      display: inline-flex;
      align-items: center;
      border-radius: 6px;
      border: none;
      background: var(--background-light-blue);
      color: var(--email-blue);
      font-size: 12px;
      padding-inline: 8px;
      height: 22px;
      line-height: 20px;
    }

    .ant-select-selection-item-content {
      color: var(--email-blue);
      font-size: 12px;
    }

    .ant-select-selection-item-remove,
    .ant-select-selection-item-remove > .anticon {
      color: var(--email-blue);
      font-size: 11px;
    }

    .ant-select-selection-item-remove:hover,
    .ant-select-selection-item-remove:hover > .anticon {
      color: var(--email-blue);
    }
  }

  &&&.ant-select.ant-select-disabled {
    cursor: default;

    .ant-select-selector {
      background: var(--background-gray);
      cursor: default;
    }

    .ant-select-selection-item {
      background: var(--background-gray);
      color: var(--text-primary);
      -webkit-text-fill-color: var(--text-primary);
      opacity: 1;
      cursor: default;
    }

    .ant-select-selection-item-content {
      color: var(--text-primary);
    }

    .ant-select-arrow,
    .ant-select-clear,
    .ant-select-selection-item-remove {
      display: none;
    }
  }
`;

const EmailMatchingClassificationMatchingFields = ({
  selectedCommunityId,
  communityOptions,
  isLoadingCommunities,
  onCommunityChange,
  shouldShowCommunityWarning,
  aiSuggestedCommunityLabel,
  selectedSenderType,
  onSenderTypeChange,
  shouldShowSenderTypeWarning,
  aiSenderType,
  selectedDeliveryMethod,
  onDeliveryMethodChange,
  shouldShowDeliveryMethodWarning,
  aiDeliveryMethod,
  selectedEmailTypeValues,
  onEmailTypesSelect,
  shouldShowCommunicationTypesWarning,
  aiSuggestedCommunicationTypesText,
  disabled,
}: Props) => {
  return (
    <MatchingFields>
      <FullRow>
        <FieldLabel>Community</FieldLabel>
        <FullWidthSelect
          dataTestId="email-matching-detail-community-select"
          className="rectangular"
          value={selectedCommunityId}
          options={communityOptions}
          placeholder="Select Community"
          onChange={onCommunityChange}
          loading={isLoadingCommunities}
          showSearch
          optionFilterProp="label"
          allowClear
          disabled={disabled}
        />
        {shouldShowCommunityWarning && aiSuggestedCommunityLabel && (
          <FieldWarning data-test-id="email-matching-detail-community-ai-suggested">{`AI Suggested: ${aiSuggestedCommunityLabel}`}</FieldWarning>
        )}
      </FullRow>

      <div>
        <FieldLabel>Sender Type</FieldLabel>
        <FullWidthSelect
          dataTestId="email-matching-detail-sender-type-select"
          className="rectangular"
          value={selectedSenderType}
          options={EMAIL_MATCHING_SENDER_TYPE_OPTIONS}
          placeholder="Select Sender Type"
          onChange={onSenderTypeChange}
          allowClear
          disabled={disabled}
        />
        {shouldShowSenderTypeWarning &&
          aiSenderType !== undefined &&
          EMAIL_MATCHING_SENDER_TYPE_LABELS[aiSenderType] && (
            <FieldWarning data-test-id="email-matching-detail-sender-type-ai-suggested">{`AI Suggested: ${EMAIL_MATCHING_SENDER_TYPE_LABELS[aiSenderType]}`}</FieldWarning>
          )}
      </div>

      <div>
        <FieldLabel>Delivery Method</FieldLabel>
        <FullWidthSelect
          dataTestId="email-matching-detail-delivery-method-select"
          className="rectangular"
          value={selectedDeliveryMethod}
          options={EMAIL_MATCHING_DELIVERY_METHOD_OPTIONS}
          placeholder="Select Delivery Method"
          onChange={onDeliveryMethodChange}
          allowClear
          disabled={disabled}
        />
        {shouldShowDeliveryMethodWarning &&
          aiDeliveryMethod !== undefined &&
          EMAIL_MATCHING_DELIVERY_METHOD_LABELS[aiDeliveryMethod] && (
            <FieldWarning data-test-id="email-matching-detail-delivery-method-ai-suggested">{`AI Suggested: ${EMAIL_MATCHING_DELIVERY_METHOD_LABELS[aiDeliveryMethod]}`}</FieldWarning>
          )}
      </div>

      <FullRow>
        <FieldLabel>Email Types</FieldLabel>
        <EmailTypesMultiSelect
          dataTestId="email-matching-detail-email-types-select"
          mode="multiple"
          value={selectedEmailTypeValues}
          options={EMAIL_MATCHING_COMMUNICATION_TYPE_OPTIONS}
          onChange={onEmailTypesSelect}
          placeholder="Add email type..."
          showSearch
          optionFilterProp="label"
          allowClear
          disabled={disabled}
        />
        {shouldShowCommunicationTypesWarning && aiSuggestedCommunicationTypesText && (
          <FieldWarning data-test-id="email-matching-detail-email-types-ai-suggested">{`AI Suggested: ${aiSuggestedCommunicationTypesText}`}</FieldWarning>
        )}
      </FullRow>
    </MatchingFields>
  );
};

export default EmailMatchingClassificationMatchingFields;
